import React, { useRef, useState } from "react";
import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type SubmitState,
} from "@services/drybulkCargoOperationsApi";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import SubmitVarianceDialog from "../components/SubmitVarianceDialog";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  SUBMIT_FROM_STATUSES,
} from "../constants";
import { problemDetail } from "../utils/problemDetail";
import { useSubmitCargoOperation } from "./useSubmitCargoOperation";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.submit`;

interface UseSubmitOperationFlowArgs {
  operationId: string;
  /** The status the server returned, compared and never derived. */
  status: string | null;
  /** The host pauses its polling while the dialog is open (DBCO-0031 D6). */
  onDialogOpenChange?: (open: boolean) => void;
  onSubmitted?: (state: SubmitState) => void;
}

/**
 * DBCO-0031 (R-09, R-03) - Submit for a Draft (DBCO-0036: or a Rejected operation, the resubmit), as a flow without a button of its own (DBCO-0065 D3: the action bar
 * owns the button). On submit it reads the precheck fresh: a status that is no longer DRAFT or REJECTED stops with a notice;
 * Remained 0 submits at once, the only no-dialog case; anything else (either direction, or no figure) asks once,
 * and the user can proceed or cancel. It never blocks (R-03). It cannot submit twice: a synchronous guard stops a
 * second activation, and the dialog stays open, with its confirm disabled and Cancel ignored, until the submit
 * settles (success or error), which then closes it.
 */
export const useSubmitOperationFlow = ({
  operationId,
  status,
  onDialogOpenChange,
  onSubmitted,
}: UseSubmitOperationFlowArgs) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const submit = useSubmitCargoOperation(operationId, onSubmitted);
  const [checking, setChecking] = useState(false);
  // Synchronous: React state alone lets two quick activations both reach the mutation.
  const inFlight = useRef(false);
  const [variance, setVariance] = useState<{ remained: number | null } | null>(null);

  const setDialog = (next: { remained: number | null } | null) => {
    setVariance(next);
    onDialogOpenChange?.(next !== null);
  };

  const startSubmit = () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    submit.mutate(undefined, {
      onSettled: () => {
        inFlight.current = false;
        setDialog(null);
      },
    });
  };

  const onSubmit = async () => {
    if (inFlight.current) {
      return;
    }
    setChecking(true);
    try {
      const precheck = await queryClient.fetchQuery({
        queryKey: [...DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId), "submit-precheck"],
        queryFn: () => drybulkCargoOperationsService.submitPrecheck(operationId),
        staleTime: 0,
      });
      if (!SUBMIT_FROM_STATUSES.includes(precheck.status)) {
        notify({ type: "info", title: t(`${L}.moved-on`) });
        void queryClient.invalidateQueries({
          queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId),
        });
      } else if (precheck.totalQuantityRemained === 0) {
        startSubmit();
      } else {
        setDialog({ remained: precheck.totalQuantityRemained });
      }
    } catch (error) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemDetail(error) ?? t("common.error"),
      });
    } finally {
      setChecking(false);
    }
  };

  const dialog = React.createElement(SubmitVarianceDialog, {
    open: variance !== null,
    remained: variance?.remained ?? null,
    isSubmitting: submit.isPending,
    onCancel: () => {
      if (!submit.isPending) {
        setDialog(null);
      }
    },
    onConfirm: startSubmit,
  });

  return {
    canSubmit: SUBMIT_FROM_STATUSES.includes(status ?? ""),
    onSubmit: () => void onSubmit(),
    pending: checking || submit.isPending || variance !== null,
    dialog,
  };
};
